import { DB_NAME, DB_VERSION } from "@/config/db.js"

class IDB {
    constructor() {
        if (IDB.instance) return IDB.instance;

        this.dbName = DB_NAME;
        this.version = DB_VERSION;
        this.db = null;

        this.currentStore = '';
        this.condition = null;
        this.indexMap = {
            productState: [
                { name: 'id', fields: ['id'] },
            ],
            productDetail: [
                { name: 'itemid_shopid_platform', fields: ['itemid', 'shopid', 'platform'] },
                { name: 'itemid_shopid', fields: ['itemid', 'shopid'] },
                { name: 'shopid_classify_str', fields: ['shopid', 'classify_str'] },
                { name: 'itemid', fields: ['itemid'] },
                { name: 'platform', fields: ['platform'] },
                { name: 'shopid', fields: ['shopid'] },
                { name: 'classify_str', fields: ['classify_str'] },
            ]
        };

        this._storeKeyPathCache = {};

        IDB.instance = this;
    }

    async openDB() {
        let user = localStorage.getItem("user-info") || null;
        user = user ? JSON.parse(user) : { userName: 'default' };

        if (this.db) return this.db;

        return new Promise((resolve, reject) => {
            const request = indexedDB.open(`${this.dbName}_${user.userName}`, this.version);
        
            request.onupgradeneeded = (e) => {
                const db = e.target.result;

                if (!db.objectStoreNames.contains('productState')) {
                    db.createObjectStore('productState', { keyPath: 'id' });
                }

                if (!db.objectStoreNames.contains('productDetail')) {
                    const store = db.createObjectStore('productDetail', {
                        keyPath: 'pid',
                        autoIncrement: true
                    });
                    store.createIndex('itemid', 'itemid');  // 商品ID
                    store.createIndex('platform', 'platform');  // 商品来源
                    store.createIndex('shopid', 'shopid');  //  商家ID
                    store.createIndex('classify_str', 'classify_str'); // 商品分类
                    store.createIndex('itemid_shopid', ['itemid', 'shopid']); // 商品ID和商家ID
                    store.createIndex('shopid_classify_str', ['shopid', 'classify_str']); // 商家ID和分类
                    store.createIndex('itemid_shopid_platform', ['itemid', 'shopid', 'platform']); // 商品ID、商家ID和来源
                }
            };

            request.onsuccess = () => {
                this.db = request.result;
                resolve(this.db);
            };

            request.onerror = () => reject(request.error);
        });
    }

    table(storeName) {
        if (!this.indexMap[storeName]) {
            throw new Error(`未定义的表名：${storeName}`);
        }
        this.currentStore = storeName;
        return this;
    }

    where(condition) {
        if (typeof condition !== 'object' || Array.isArray(condition)) {
            throw new Error("where() 参数必须是对象");
        }
        this.condition = condition;
        return this;
    }

    _getStore(mode = 'readonly') {
        const store = this.db.transaction(this.currentStore, mode).objectStore(this.currentStore);
        if (!this._storeKeyPathCache[this.currentStore]) {
            this._storeKeyPathCache[this.currentStore] = store.keyPath;
        }
        return store;
    }

    _getKeyPath() {
        return this._storeKeyPathCache[this.currentStore];
    }

    _findBestIndex() {
        const indexes = this.indexMap[this.currentStore] || [];
        const conditionKeys = Object.keys(this.condition || {}).sort();

        for (const idx of indexes) {
            const indexKeys = [...idx.fields].sort();
            if (
                conditionKeys.length === indexKeys.length &&
                conditionKeys.every((key, i) => key === indexKeys[i])
            ) {
                return {
                    indexName: idx.name,
                    values: idx.fields.map(f => this.condition[f])
                };
            }
        }

        // 最小匹配（单字段）
        for (const idx of indexes) {
            if (idx.fields.length === 1 && this.condition.hasOwnProperty(idx.fields[0])) {
                return {
                    indexName: idx.name,
                    values: [this.condition[idx.fields[0]]]
                };
            }
        }

        console.warn(`[IDB] 未找到匹配索引，将使用 getAll fallback，字段为:`, conditionKeys);
        return null;
    }

    async get() {
        await this.openDB();
        const store = this._getStore('readonly');

        const keyPath = this._getKeyPath();
        let result = null;

        // 主键查找
        if (Object.keys(this.condition).length === 1 && keyPath in this.condition) {
            result = await new Promise((resolve, reject) => {
                const req = store.get(this.condition[keyPath]);
                req.onsuccess = () => resolve(req.result);
                req.onerror = () => reject(req.error);
            });
        } else {
            const matchIndex = this._findBestIndex();
            if (matchIndex) {
                result = await new Promise((resolve, reject) => {
                    const index = store.index(matchIndex.indexName);
                    const key = matchIndex.values.length === 1 ? matchIndex.values[0] : matchIndex.values;
                    const req = index.get(key);
                    req.onsuccess = () => resolve(req.result);
                    req.onerror = () => reject(req.error);
                });
            } else {
                result = await new Promise((resolve, reject) => {
                    const req = store.getAll();
                    req.onsuccess = () => {
                        const all = req.result;
                        if (!all?.length) return resolve(null);
                        const res = all.find(item =>
                            Object.keys(this.condition).every(key => item[key] === this.condition[key])
                        );
                        resolve(res);
                    };
                    req.onerror = () => reject(req.error);
                });
            }
        }

        this.condition = null;
        return result;
    }

    async getAll() {
        await this.openDB();
        const store = this._getStore('readonly');

        let result = [];

        const matchIndex = this._findBestIndex();
        if (matchIndex) {
            result = await new Promise((resolve, reject) => {
                const index = store.index(matchIndex.indexName);
                const key = matchIndex.values.length === 1 ? matchIndex.values[0] : matchIndex.values;
                const req = index.getAll(key);
                req.onsuccess = () => resolve(req.result);
                req.onerror = () => reject(req.error);
            });
        } else {
            result = await new Promise((resolve, reject) => {
                const req = store.getAll();
                req.onsuccess = () => {
                    const all = req.result;
                    if (!all?.length) return resolve([]);
                    const res = all.filter(item =>
                        Object.keys(this.condition).every(key => item[key] === this.condition[key])
                    );
                    resolve(res);
                };
                req.onerror = () => reject(req.error);
            });
        }

        this.condition = null;
        return result;
    }

    async put(data) {
        await this.openDB();
        const store = this._getStore('readwrite');
        const keyPath = this._getKeyPath();

        if (this.condition) {
            const matchIndex = this._findBestIndex();
            if (matchIndex) {
                const index = store.index(matchIndex.indexName);
                const key = matchIndex.values.length === 1 ? matchIndex.values[0] : matchIndex.values;

                const existing = await new Promise((resolve, reject) => {
                    const req = index.get(key);
                    req.onsuccess = () => resolve(req.result);
                    req.onerror = () => reject(req.error);
                });

                if (existing && existing[keyPath]) {
                    data[keyPath] = existing[keyPath]; // 用主键更新
                }
            }
        }

        const result = await new Promise((resolve, reject) => {
            const req = store.put(data);
            req.onsuccess = () => resolve(true);
            req.onerror = () => reject(req.error);
        });

        this.condition = null;
        return result;
    }

    async delete() {
        await this.openDB();
        const store = this._getStore('readwrite');
        const keyPath = this._getKeyPath();

        const matchIndex = this._findBestIndex();
        let deleted = false;

        if (matchIndex) {
            deleted = await new Promise((resolve, reject) => {
                const index = store.index(matchIndex.indexName);
                const key = matchIndex.values.length === 1 ? matchIndex.values[0] : matchIndex.values;
                const req = index.getKey(key);
                req.onsuccess = () => {
                    if (req.result !== undefined) {
                        const delReq = store.delete(req.result);
                        delReq.onsuccess = () => resolve(true);
                        delReq.onerror = () => reject(delReq.error);
                    } else {
                        resolve(false);
                    }
                };
                req.onerror = () => reject(req.error);
            });
        } else {
            deleted = await new Promise((resolve, reject) => {
                const req = store.getAll();
                req.onsuccess = () => {
                    const item = req.result.find(item =>
                        Object.keys(this.condition).every(key => item[key] === this.condition[key])
                    );
                    if (!item) return resolve(false);
                    const delReq = store.delete(item[keyPath]);
                    delReq.onsuccess = () => resolve(true);
                    delReq.onerror = () => reject(delReq.error);
                };
                req.onerror = () => reject(req.error);
            });
        }

        this.condition = null;
        return deleted;
    }
    
    async deleteAll() {
        await this.openDB();
        const store = this._getStore('readwrite');
        const keyPath = this._getKeyPath();

        const matchIndex = this._findBestIndex();
        let deleted = 0;

        if (matchIndex) {
            // 通过索引 cursor 遍历
            deleted = await new Promise((resolve, reject) => {
                const index = store.index(matchIndex.indexName);
                const key = matchIndex.values.length === 1 ? matchIndex.values[0] : matchIndex.values;
                const range = IDBKeyRange.only(key);
                const req = index.openCursor(range);
                let count = 0;

                req.onsuccess = (e) => {
                    const cursor = e.target.result;
                    if (cursor) {
                        store.delete(cursor.primaryKey);
                        count++;
                        cursor.continue();
                    } else {
                        resolve(count);
                    }
                };
                req.onerror = () => reject(req.error);
            });
        } else {
            // fallback：全表筛选删除
            deleted = await new Promise((resolve, reject) => {
                const req = store.openCursor();
                let count = 0;

                req.onsuccess = (e) => {
                    const cursor = e.target.result;
                    if (cursor) {
                        const item = cursor.value;
                        const match = Object.keys(this.condition).every(key => item[key] === this.condition[key]);
                        if (match) {
                            store.delete(cursor.primaryKey);
                            count++;
                        }
                        cursor.continue();
                    } else {
                        resolve(count);
                    }
                };
                req.onerror = () => reject(req.error);
            });
        }

        this.condition = null;
        return deleted; // 返回删除的数量
    }

    // 更新指定字段，仅更新符合 where 条件的记录
    async update(data) {
        await this.openDB();
        const store = this._getStore('readwrite');
        const keyPath = this._getKeyPath();

        let target = null;

        const matchIndex = this._findBestIndex();
        if (matchIndex) {
            target = await new Promise((resolve, reject) => {
                const index = store.index(matchIndex.indexName);
                const key = matchIndex.values.length === 1 ? matchIndex.values[0] : matchIndex.values;
                const req = index.get(key);
                req.onsuccess = () => resolve(req.result);
                req.onerror = () => reject(req.error);
            });
        } else {
            target = await new Promise((resolve, reject) => {
                const req = store.getAll();
                req.onsuccess = () => {
                    const item = req.result.find(item =>
                        Object.keys(this.condition).every(key => item[key] === this.condition[key])
                    );
                    resolve(item || null);
                };
                req.onerror = () => reject(req.error);
            });
        }

        this.condition = null;

        if (!target || !target[keyPath]) return false;

        const merged = { ...target, ...data };

        return new Promise((resolve, reject) => {
            const req = store.put(merged);
            req.onsuccess = () => resolve(true);
            req.onerror = () => reject(req.error);
        });
    }

    // 清空当前表
    async clear() {
        await this.openDB();
        const store = this._getStore('readwrite');

        return new Promise((resolve, reject) => {
            const req = store.clear();
            req.onsuccess = () => resolve(true);
            req.onerror = () => reject(req.error);
        });
    }

}

const idb = new IDB();
export default idb;